import {
  generateNumbersInRange,
  randomiseList,
} from '@freelancer/datastore/testing';
import { RoleApi } from 'api-typings/common/common';
import { defaultReputations } from '../freelancer-reputations/freelancer-reputations.seed';
import {
  generateFreelancerReputationDataWithRehireObjects,
  GenerateFreelancerReputationDataWithRehireOption,
  GenerateFreelancerReputationDataWithRehireOptions,
  generateReputationDataWithRehireObject,
} from '../reputation/reputation-data.seed';
import { FreelancerReputationWithRehire } from './freelancer-reputations-rehire-rates.model';

interface Range {
  readonly min: number;
  readonly max: number;
}

export interface GenerateFreelancerReputationsWithRehireOptions {
  readonly freelancerIds: readonly number[];
  readonly reputationOptions?: GenerateFreelancerReputationDataWithRehireOptions;
  readonly earningsScore?: Range;
}

/** Keep in sync with generateFreelancerReputationsObjects */
export function generateFreelancerReputationsRehireRatesObjects({
  freelancerIds,
  reputationOptions = {},
  earningsScore = { min: 3, max: 10 },
}: GenerateFreelancerReputationsWithRehireOptions): readonly FreelancerReputationWithRehire[] {
  const entireHistories = generateFreelancerReputationDataWithRehireObjects({
    ...reputationOptions,
    seed: 'entireHistory',
    numberToMake: freelancerIds.length,
  });

  const earningsScores = randomiseList(
    generateNumbersInRange(
      earningsScore.min,
      earningsScore.max,
      freelancerIds.length,
    ),
    'earningsScore',
  );

  return freelancerIds.map((freelancerId, i) => ({
    id: freelancerId,
    role: RoleApi.FREELANCER,
    // Set value to 0 if you've not done any work.
    earningsScore: entireHistories[i].all === 0 ? 0 : earningsScores[i],
    entireHistory: entireHistories[i],
  }));
}

export interface GenerateFreelancerReputationWithRehireOptions {
  readonly freelancerId: number;
  readonly reputationOptions?: Partial<GenerateFreelancerReputationDataWithRehireOption>;
  readonly earningsScore?: number;
}

/** Keep in sync with generateFreelancerReputationsObject */
export function generateFreelancerReputationsRehireRatesObject({
  freelancerId,
  reputationOptions = {},
  earningsScore = 7,
}: GenerateFreelancerReputationWithRehireOptions): FreelancerReputationWithRehire {
  return {
    id: freelancerId,
    earningsScore,
    role: RoleApi.FREELANCER,
    entireHistory: generateReputationDataWithRehireObject({
      ...defaultReputations,
      rehirePercentage: 0.7,
      ...reputationOptions,
    }),
  };
}
